import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import toast from 'react-hot-toast';

const AUDIO_URLS = [
  '/jingle-bells.mp3',
  'jingle-bells.mp3',
  'https://media.vocaroo.com/mp3/17rcG6Pwja05'
];

export default function AudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [showControls, setShowControls] = useState(true);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    audio.preload = 'auto';
    audio.loop = true;
    audio.volume = 0.3;

    const loadAudio = async () => {
      for (const url of AUDIO_URLS) {
        try {
          audio.src = url;
          await new Promise((resolve, reject) => {
            const onCanPlay = () => {
              audio.removeEventListener('canplaythrough', onCanPlay);
              audio.removeEventListener('error', onError);
              resolve(true);
            };
            const onError = () => {
              audio.removeEventListener('canplaythrough', onCanPlay);
              audio.removeEventListener('error', onError);
              reject();
            };
            audio.addEventListener('canplaythrough', onCanPlay, { once: true });
            audio.addEventListener('error', onError, { once: true });
            audio.load();
          });
          setIsLoaded(true);
          
          // Restaurar estado anterior
          const savedState = localStorage.getItem('audioPlayerState');
          if (savedState === 'playing') {
            await audio.play().catch(() => {});
            setIsPlaying(true);
          }
          return;
        } catch (err) {
          console.warn(`Failed to load audio from ${url}:`, err);
          continue;
        }
      }
    };

    loadAudio().catch(() => {
      setIsLoaded(false);
    });

    const handleVisibilityChange = () => {
      if (!audio) return;
      if (document.hidden) {
        audio.pause();
        setIsPlaying(false);
      } else if (isPlaying) {
        audio.play().catch(() => {});
      }
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowControls(
        currentScrollY < lastScrollY.current || 
        currentScrollY < 100 || 
        (window.innerHeight + currentScrollY) >= document.documentElement.scrollHeight - 100
      );
      lastScrollY.current = currentScrollY;

      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => setShowControls(true), 1500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        localStorage.setItem('audioPlayerState', 'paused');
      } else {
        await audio.play();
        setIsPlaying(true);
        localStorage.setItem('audioPlayerState', 'playing');
      }
    } catch (err) {
      console.error('Error toggling audio:', err);
      toast.error('Error al reproducir el audio');
      setIsPlaying(false);
      localStorage.setItem('audioPlayerState', 'paused');
    }
  };

  if (!isLoaded) return null;

  return (
    <button
      onClick={togglePlay}
      className={`fixed bottom-20 right-4 z-[1000] bg-primary hover:bg-primary-dark text-white p-3 rounded-full shadow-lg transition-all transform ${
        showControls ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
      }`}
      aria-label={isPlaying ? 'Pausar música' : 'Reproducir música'}
    >
      {isPlaying ? <Volume2 size={24} /> : <VolumeX size={24} />}
    </button>
  );
}